import React from "react";
import styled from "styled-components";

import { Icon } from "js/Components/Icon";
import { themeColors } from "js/react/sharedStyles";
import { t } from "js/i18n";

export interface RecentSearchesDropdownProps {
    recentSearches: string[];
    query?: string;
    showSearchInFolder?: boolean;
    activeIndex?: number;
    onSelectRecentSearch: (term: string) => void;
    onSearchInSelectedFolder?: () => void;
}

/** Shared with SearchAndAdd so arrow-key indices match the rendered rows. */
export function filterRecentSearches(recentSearches: string[], query?: string) {
    const normalizedQuery = query?.trim().toLowerCase() ?? "";
    if (!normalizedQuery) {
        return recentSearches;
    }

    return recentSearches.filter(term => term.toLowerCase().includes(normalizedQuery));
}

const Dropdown = styled.div.attrs({
    "data-test-id": "library-recent-searches-dropdown",
    role: "listbox",
})`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    box-sizing: border-box;
    padding: 5px 0;
    background: white;
    border: 1px solid ${themeColors.lightGray};
    border-radius: 3px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
`;

const Item = styled.button.attrs({
    type: "button",
    role: "option",
})`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    margin: 0;
    padding: 12px;
    border: none;
    background: transparent;
    color: ${themeColors.darkGray};
    font-family: "Source Sans Pro", sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
    text-align: left;
    cursor: pointer;

    &:hover,
    &:focus,
    &[aria-selected="true"] {
        background: ${themeColors.lightGray};
        outline: none;
    }

    .MuiIcon-root,
    .material-icons,
    .material-symbols-outlined {
        color: #777;
        font-size: 20px;
    }
`;

const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 5px 12px 5px 16px;
    color: #888;
    font-family: "Source Sans Pro", sans-serif;
    font-size: 10px;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    margin: 4px 0;
    background: ${themeColors.lightGray};
`;

const ItemLabel = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export function RecentSearchesDropdown({
    recentSearches,
    query,
    showSearchInFolder = false,
    activeIndex = -1,
    onSelectRecentSearch,
    onSearchInSelectedFolder,
}: RecentSearchesDropdownProps) {
    const matchingRecentSearches = filterRecentSearches(recentSearches, query);
    const hasRecentSearches = matchingRecentSearches.length > 0;
    const hasFolderOption = Boolean(showSearchInFolder && onSearchInSelectedFolder);

    if (!hasRecentSearches && !hasFolderOption) {
        return null;
    }

    return (
        <Dropdown
            onMouseDown={event => {
                // Keep input focus while clicking options so blur does not race the click.
                event.preventDefault();
            }}
        >
            {hasFolderOption && (
                <>
                    <Item
                        id="library-recent-option-0"
                        data-test-id="library-search-in-folder-option"
                        aria-selected={activeIndex === 0}
                        onClick={onSearchInSelectedFolder}
                    >
                        <Icon>folder</Icon>
                        <ItemLabel>{t("Search in selected folder")}</ItemLabel>
                    </Item>
                    {hasRecentSearches && <Divider />}
                </>
            )}
            {hasRecentSearches && (
                <>
                    <SectionLabel>{t("Recent searches")}</SectionLabel>
                    {matchingRecentSearches.map((term, recentIndex) => {
                        const index = hasFolderOption ? recentIndex + 1 : recentIndex;
                        return (
                            <Item
                                key={term}
                                id={`library-recent-option-${index}`}
                                data-test-id="library-recent-search-item"
                                aria-selected={activeIndex === index}
                                onClick={() => onSelectRecentSearch(term)}
                            >
                                <Icon>history</Icon>
                                <ItemLabel>{term}</ItemLabel>
                            </Item>
                        );
                    })}
                </>
            )}
        </Dropdown>
    );
}
